/**
 * usePlatformRouter - Shared Platform-Routing Boilerplate
 *
 * Extracts the common pattern from useTransformApi, useAnalyzeApi,
 * useClusterApi, and useMediaApi: platform detection, capabilities ref,
 * ref update effect, getBackend callback, and availability computation.
 *
 * Each domain hook calls usePlatformRouter with a config describing
 * how to resolve capabilities and availability for its domain.
 *
 * @module @humanizer/studio/hooks/usePlatformRouter
 */

import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { usePlatformOptional } from '../contexts/PlatformContext';
import type { PlatformContextValue } from '../contexts/PlatformContext';

export type Backend = 'local' | 'cloud' | 'byok' | 'none';

export interface BaseCapabilities {
  canUseLocal: boolean;
  canUseCloud: boolean;
  preferredBackend: Backend;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface PlatformRouterConfig<TExtras extends Record<string, unknown> = {}> {
  /** Compute capabilities + extras from platform context (or defaults when null) */
  resolveCapabilities(platform: PlatformContextValue | null, isElectron: boolean): BaseCapabilities & TExtras;
  /** Compute the availability triplet for the returned object */
  resolveAvailability(platform: PlatformContextValue | null, isElectron: boolean): {
    isAvailable: boolean;
    backend: Backend;
    unavailableReason: string | null;
  };
  /** Extract platform-dependent values for the useEffect dependency array */
  platformDeps(platform: PlatformContextValue | null): unknown[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface PlatformRouterResult<TExtras extends Record<string, unknown> = {}> {
  isElectron: boolean;
  getBackend: () => Backend;
  capabilitiesRef: React.MutableRefObject<BaseCapabilities & TExtras>;
  isAvailable: boolean;
  backend: Backend;
  unavailableReason: string | null;
  cloudApi: ReturnType<typeof useApi>;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export function usePlatformRouter<TExtras extends Record<string, unknown> = {}>(
  config: PlatformRouterConfig<TExtras>
): PlatformRouterResult<TExtras> {
  const cloudApi = useApi();
  const platform = usePlatformOptional();

  // Platform detection (stable - never changes after mount)
  const isElectron = useMemo(
    () => typeof window !== 'undefined' && !!window.isElectron,
    []
  );

  // Use refs for values that change but shouldn't cause callback regeneration
  const capabilitiesRef = useRef(config.resolveCapabilities(null, isElectron));

  // Update ref when platform capabilities change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    capabilitiesRef.current = config.resolveCapabilities(platform, isElectron);
  }, [isElectron, ...config.platformDeps(platform)]);

  // Helper to get current backend at call time
  const getBackend = useCallback((): Backend => {
    return capabilitiesRef.current.preferredBackend;
  }, []);

  // Compute current availability for the returned object
  const { isAvailable, backend, unavailableReason } = config.resolveAvailability(platform, isElectron);

  return {
    isElectron,
    getBackend,
    capabilitiesRef,
    isAvailable,
    backend,
    unavailableReason,
    cloudApi,
  };
}
